import { useCallback, useRef, useState } from "react";
import { VtRow } from "./vtModel";

interface DraftState {
  saved: VtRow[];
  draft: VtRow[];
  history: VtRow[][];
  future: VtRow[][];
}

const HISTORY = 100;
/** Edits to one field this close together undo as one step. */
const COALESCE_MS = 1500;

/** The tab's working copy: every edit stays a draft until saved, with undo
 *  and redo across all of them. */
export function useVtDraft() {
  const [state, setState] = useState<DraftState>({ saved: [], draft: [], history: [], future: [] });
  const last = useRef<{ key: string | null; at: number }>({ key: null, at: 0 });

  const reset = useCallback((rows: VtRow[]) => {
    last.current = { key: null, at: 0 };
    setState({ saved: rows, draft: rows.map((r) => ({ ...r })), history: [], future: [] });
  }, []);

  /** Change the draft; ``coalesce`` names the field being typed into. */
  const mutate = useCallback((fn: (draft: VtRow[]) => VtRow[], coalesce?: string) => {
    const now = Date.now();
    const merge = !!coalesce && coalesce === last.current.key && now - last.current.at < COALESCE_MS;
    last.current = { key: coalesce ?? null, at: now };
    setState((s) => ({
      ...s,
      draft: fn(s.draft),
      history: merge ? s.history : [...s.history.slice(-(HISTORY - 1)), s.draft],
      future: [],
    }));
  }, []);

  const updateRow = useCallback((key: string, patch: Partial<VtRow>, coalesce?: string) => {
    mutate((d) => d.map((r) => (r.key === key ? { ...r, ...patch } : r)), coalesce);
  }, [mutate]);

  const undo = useCallback(() => {
    last.current = { key: null, at: 0 };
    setState((s) => (s.history.length
      ? { ...s, draft: s.history[s.history.length - 1], history: s.history.slice(0, -1), future: [s.draft, ...s.future] }
      : s));
  }, []);

  const redo = useCallback(() => {
    last.current = { key: null, at: 0 };
    setState((s) => (s.future.length
      ? { ...s, draft: s.future[0], future: s.future.slice(1), history: [...s.history, s.draft] }
      : s));
  }, []);

  return {
    saved: state.saved,
    draft: state.draft,
    canUndo: state.history.length > 0,
    canRedo: state.future.length > 0,
    reset, mutate, updateRow, undo, redo,
  };
}
